import FuseLayout from "@fuse/core/FuseLayout";
import FuseTheme from "@fuse/core/FuseTheme";
import rtlPlugin from "stylis-plugin-rtl";
import createCache, { Options } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { LanguageType, selectCurrentLanguageDirection } from "app/store/i18nSlice";
import { selectMainTheme } from "@fuse/core/FuseSettings/fuseSettingsSlice";
import AuthenticationProvider from "./auth/AuthenticationProvider";
import { fetchUrlTOken, formaAppLanguage, getSalesContext, getUrlParams } from "./store/dataUtils";
import { applyAppearanceCssVars } from "./configs/appearance";
import { useAppDispatch, useAppSelector } from "app/store/hooks";
import themeLayouts from "app/theme-layouts/themeLayouts";
import { changeLanguage } from "app/store/i18nSlice";
import { useSelector } from "react-redux";
import withAppProviders from "./withAppProviders";
import { SnackbarProvider } from "notistack";
import { useEffect } from "react";
import { useSocketStore } from "./store/socketStore";
import { ChatMessage } from "./models/chat-message";
import { useSearchParams } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { useAlertDispatch } from "./store/alertDispatch";
import { appConfig } from "./configs/appConfig";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import { useDataStore } from "./store/dataStore";
import axios from "axios";

const emotionCacheOptions = {
	rtl: {
		key: "muirtl",
		stylisPlugins: [rtlPlugin],
		insertionPoint: document.getElementById("emotion-insertion-point")
	},
	ltr: {
		key: "muiltr",
		stylisPlugins: [],
		insertionPoint: document.getElementById("emotion-insertion-point")
	}
};

/** The main App component */
function App() {
	const { driver, warehouse, warehouses, chatId, shipment, language, sidebarEnabled } = getUrlParams();
	const { token, env } = fetchUrlTOken();
	if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
	if (env) axios.defaults.headers.common["env-request-call"] = env;
	// REST calls carry the same URL-declared scope the socket handshake fixes.
	// Only the NEW contract (`warehouses` plural) declares scope — legacy
	// launches send no scope headers and stay unscoped, as before.
	if (warehouses?.length) {
		axios.defaults.headers.common["x-comms-warehouses"] = warehouses.join(",");
		if (driver) axios.defaults.headers.common["x-comms-driver"] = driver;
	}
	const langDirection = useAppSelector(selectCurrentLanguageDirection);
	const params = new URLSearchParams(window.location.search);
	const { messageSocket, setIsConnected } = useSocketStore();
	const mainTheme = useSelector(selectMainTheme);
	const { dispatchAlert } = useAlertDispatch();
	const { setVersion } = useDataStore();
	const { t: trl } = useTranslation();
	const dispatch = useAppDispatch();
	const url = appConfig.baseurl + "/chats";

	console.log({ url });

	function handleLanguageChange(lng: LanguageType) {
		dispatch(changeLanguage(lng.id));
	}

	function appendToUri(param: string) {
		if (!params.get("chatId")) {
			const currentSearch = window.location.search;
			window.history.replaceState(null, "", `${currentSearch}${param}`);
			window.location.reload();
		}
	}

	async function handleInitPlatform() {
		const {
			data: { app_version }
		} = await axios.get(`${appConfig.baseurl}/platform`);
		setVersion(app_version);
	}

	async function handleInitChat() {
		// Sales mode (URL-scoped customer ↔ sales rep chat) needs no driver/warehouse params
		const salesContext = getSalesContext();
		if (!salesContext && !sidebarEnabled && (!driver || !warehouse))
			return dispatchAlert("error", trl("comms_paramsMissing"), 4000);
		console.log("initializing chat");

		let socket = io(url, {
			auth: {
				token: token || undefined,
				env: env || undefined,
				// Legacy context fields — exactly what pre-restructure clients sent
				driver: driver || "n/a",
				warehouse: warehouse || "n/a",
				shipment: shipment || "n/a",
				// New-contract scope fields — absent on legacy launches
				warehouses: warehouses?.length ? warehouses.join(",") : undefined,
				shipments: salesContext?.shipments.join(",") || undefined,
				role: salesContext?.role || undefined,
				chatId: chatId || undefined
			},
			transports: ["websocket", "polling", "webtransport"],
			reconnectionAttempts: 5,
			reconnectionDelay: 1000,
			reconnection: true,
			timeout: 20000,
			upgrade: true
		});

		socket.on("connect", async () => {
			console.log("socket initialized", { connected: socket?.id });
			useSocketStore.setState({ messageSocket: socket });
			setIsConnected(true);
		});

		socket.on("disconnect", (reason) => {
			console.log("socket disconnected", { reason });
			setIsConnected(false);
		});

		socket.on("connect_error", (err) => {
			console.error("socket connect_error", { message: err?.message });
			if (err?.message === "Authentication error") {
				setIsConnected(false);
				dispatchAlert("error", trl("comms_accessDenied"), 8000);
			}
		});

		// Critical: Re-join chat rooms after reconnection
		socket.on("reconnect", (attemptNumber) => {
			console.log("socket reconnected after", attemptNumber, "attempts");
			setIsConnected(true);

			// Re-join current chat room if in sidebar mode
			const currentRoom = useDataStore.getState().currentChatRoom;
			if (sidebarEnabled && currentRoom?._id) {
				console.log("Re-joining chat room:", currentRoom._id);
				socket.emit("chat-room::join", { chatId: currentRoom._id });

				// Re-fetch driver chat rooms if applicable
				const warehouse = useDataStore.getState().currentWarehouse;
				if (warehouse) {
					socket.emit("driver-chat-rooms::fetch", { warehouseId: warehouse });
				}
			}
		});

		socket.on("reconnect_attempt", (attemptNumber) => {
			console.log("Attempting to reconnect:", attemptNumber);
		});

		socket.on("reconnect_failed", () => {
			console.error("Failed to reconnect socket");
			dispatchAlert("error", "Connection lost. Please refresh the page.", 5000);
		});
	}

	useEffect(() => {
		applyAppearanceCssVars();
		handleLanguageChange(formaAppLanguage(language));
		handleInitPlatform();
		handleInitChat();

		return () => {
			if (messageSocket) {
				messageSocket.disconnect();
			}
		};
	}, []);

	return (
		<CacheProvider value={createCache(emotionCacheOptions[langDirection] as Options)}>
			<FuseTheme
				theme={mainTheme}
				root
			>
				<AuthenticationProvider>
					<SnackbarProvider
						maxSnack={5}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "right"
						}}
						classes={{
							containerRoot: "bottom-0 right-0 mb-52 md:mb-68 mr-8 lg:mr-80 z-99"
						}}
					>
						{/* @ts-ignore */}
						<FuseLayout layouts={themeLayouts} />
					</SnackbarProvider>
				</AuthenticationProvider>
			</FuseTheme>
		</CacheProvider>
	);
}

export default withAppProviders(App);
